// Déclaration UNIQUE des onglets — et c'est tout l'intérêt.
//
// Avant, un onglet vivait à TROIS endroits désynchronisés : la liste des
// identifiants de boutons, une ligne `display` par panneau, et une branche du
// dispatcher de chargement. Ajouter un onglet imposait trois éditions
// coordonnées, sans rien pour rattraper un oubli — et l'oubli était déjà arrivé :
// le panneau « Fournisseurs » n'avait pas sa règle de masquage et s'affichait
// sous la liste des factures au premier chargement, jusqu'au premier clic.
//
// Ici, un onglet se déclare une fois. Les panneaux non déclarés visibles sont
// masqués, donc l'oubli devient impossible plutôt qu'improbable.

// Trois familles, décidées par Mehdi. Onze onglets sur une seule barre
// débordaient sur deux lignes : ce n'était pas un problème de place mais de
// hiérarchie — rien ne disait lequel répond à quelle question.
//
// La famille se lit comme une intention :
//   argent  — « combien je dois, quand, et à qui »
//   analyse — « qu'est-ce que ça raconte »
//   sources — « d'où viennent ces données, et est-ce qu'elles arrivent encore »
export const GROUPES = [
  { id: "argent", libelle: "Argent" },
  { id: "analyse", libelle: "Analyse" },
  { id: "sources", libelle: "Sources" },
];

// L'ordre DANS un groupe est l'ordre d'affichage de sa barre.
export const ONGLETS = [
  {
    id: "today",
    libelle: "Aujourd'hui",
    pane: "accueil",
    filtres: false,
    groupe: "argent",
    // Premier onglet ET onglet par défaut : l'app doit ouvrir sur une RÉPONSE
    // (« qu'est-ce qui demande une décision ? »), pas sur un dépôt de données.
    defaut: true,
  },
  { id: "pilotage", libelle: "Pilotage", pane: "pilotage", filtres: false, groupe: "argent" },
  { id: "ech", libelle: "Échéances à venir", pane: "liste", filtres: true, tri: "echeance", groupe: "argent" },
  { id: "frais", libelle: "Frais & charges", pane: "frais", filtres: false, groupe: "argent" },
  { id: "cout", libelle: "Combien je paie", pane: "cout", filtres: false, groupe: "argent" },

  // « Graphes » et non « Analyse » : un onglet ne peut pas porter le nom du
  // groupe qui le contient sans que l'un des deux devienne illisible. Le panneau
  // ne montre que des agrégats — dépenses par mois, top fournisseurs,
  // répartition par source —, donc le nom est aussi plus juste qu'avant.
  { id: "ana", libelle: "Graphes", pane: "analyse", filtres: true, groupe: "analyse" },
  { id: "rem", libelle: "Alertes ⚠", pane: "remises", filtres: false, groupe: "analyse" },
  { id: "four", libelle: "Fournisseurs", pane: "fournisseurs", filtres: false, groupe: "analyse" },
  // Dans « Analyse » et non « Argent » : il ne dit pas ce qu'on doit, il simule
  // ce qu'un changement de grossiste rapporterait. (« Argent » est aussi plein :
  // cinq onglets, le plafond d'une barre.)
  { id: "remgros", libelle: "Remise grossiste", pane: "remisegrossiste", filtres: false, groupe: "analyse" },

  { id: "canaux", libelle: "Canaux", pane: "canaux", filtres: false, groupe: "sources" },
  { id: "col", libelle: "Collecteurs", pane: "collecteurs", filtres: false, groupe: "sources" },
  { id: "all", libelle: "Toutes", pane: "liste", filtres: true, tri: "date", groupe: "sources" },

  // Rangé dans « Sources » par défaut de mieux, et c'est un choix discutable :
  // les trois autres écrans de la famille répondent à « d'où viennent ces
  // données », celui-ci à « de quoi parlent-elles ». Il y est quand même plutôt
  // que dans Argent ou Analyse, où il serait franchement à contre-emploi, et
  // plutôt que dans une quatrième famille créée pour lui seul.
  { id: "marques", libelle: "Marques", pane: "etiquetage", filtres: false, groupe: "sources" },
];

/** Tous les panneaux existants, déduits de la table (pas d'une seconde liste à tenir). */
export const PANES = [...new Set(ONGLETS.map((o) => o.pane))];

export function ongletDefaut() {
  return ONGLETS.find((o) => o.defaut) ?? ONGLETS[0];
}

export function ongletParId(id) {
  return ONGLETS.find((o) => o.id === id) ?? ongletDefaut();
}

/**
 * Le groupe se DÉDUIT de l'onglet actif — jamais l'inverse.
 *
 * C'est l'invariant qui tient toute la navigation : `setTab` est aussi appelé
 * depuis les tuiles de l'écran d'accueil, qui sautent directement sur
 * « Alertes » ou « Fournisseurs » sans passer par la barre. Si le groupe était
 * un état à part, ces sauts laisseraient la barre allumée sur le mauvais
 * groupe — un écran qui ne correspond plus à sa propre navigation.
 */
export function groupeDeLOnglet(idOnglet) {
  return ongletParId(idOnglet).groupe;
}

/** Les onglets d'un groupe, dans leur ordre de déclaration. */
export function ongletsDuGroupe(idGroupe) {
  return ONGLETS.filter((o) => o.groupe === idGroupe);
}

/**
 * L'onglet vers lequel mène un clic sur le groupe : le premier de sa liste.
 * Prévisible plutôt que malin — mémoriser le dernier onglet visité par groupe
 * ferait qu'un même clic ne mène pas deux fois au même endroit.
 */
export function ongletDEntree(idGroupe) {
  return ongletsDuGroupe(idGroupe)[0] ?? ongletDefaut();
}

/**
 * Quels panneaux sont visibles pour cet onglet.
 * Retourne TOUS les panneaux, chacun à true ou false — c'est ce qui rend
 * l'oubli impossible : un panneau non concerné est explicitement masqué, il ne
 * dépend pas d'une règle CSS qu'on aurait pu ne pas écrire.
 */
export function panesVisibles(idOnglet) {
  const actif = ongletParId(idOnglet).pane;
  return Object.fromEntries(PANES.map((p) => [p, p === actif]));
}

/**
 * La barre de filtres doit-elle s'afficher ?
 *
 * Avant, elle était toujours visible alors que seuls deux onglets sur sept
 * l'utilisaient. Régler une plage de dates sur « Collecteurs » déclenchait un
 * rechargement qui l'ignorait, et une recherche tapée pour les Alertes restait
 * active et filtrait silencieusement la liste des factures au retour.
 */
export function filtresVisibles(idOnglet) {
  return ongletParId(idOnglet).filtres === true;
}

/**
 * Tri à appliquer en entrant dans l'onglet, ou null pour conserver le tri courant.
 * Déclaré ici plutôt qu'en dur dans le dispatcher, où il écrasait en silence le
 * tri choisi par l'utilisateur au retour sur un autre onglet.
 */
export function triDeLOnglet(idOnglet) {
  return ongletParId(idOnglet).tri ?? null;
}
